/**
 * Module dependencies.
 */

import { appRoutes } from 'app/routes';
import React, { useEffect, useMemo } from 'react';
import {
  Bitcoin,
  Bot,
  Coins,
  Folder,
  Landmark,
  LayoutDashboard,
  NotebookText,
  PiggyBank,
  Settings,
  ShieldCheck
} from 'lucide-react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { cookieStorage } from 'app/core/utils/cookie-storage';
import { MenuItem } from 'app/types/navigation';
import { sortBy } from 'lodash';

/**
 * Flat items.
 */

function flatItems(items: MenuItem[], parent?: MenuItem): MenuItem[] {
  let result: MenuItem[] = [];

  for (const item of items) {
    if (item.children) {
      result = [...result, ...flatItems(item.children, item)];
    }

    result.push({
      ...item,
      parent
    });
  }

  return result;
}

/**
 * Resolve selected keys.
 */

export function resolveSelectedKeys(pathname: string, items: MenuItem[]): MenuItem {
  let item = flatItems(items).find(item => {
    return pathname === item.pathname;
  });

  if (item) {
    return item;
  }

  item = sortBy(flatItems(items), item => item?.pathname?.length ?? 0 * -1)
    .reverse()
    .find(item => {
      return pathname.startsWith(item?.pathname ?? '');
    });

  if (item) {
    return item;
  }

  return items?.[0];
}

/**
 * Product-level navigation config (top-level areas of the app).
 */

export const menuConfig = [
  {
    key: '1',
    pathname: appRoutes.dashboard.home.base,
    icon: <LayoutDashboard />,
    label: 'menu.dashboard'
  },
  {
    key: '2',
    pathname: appRoutes.dashboard.bankAccounts.base,
    icon: <Landmark />,
    label: 'menu.bankAccounts'
  },
  {
    key: '3',
    pathname: appRoutes.dashboard.projects.base,
    icon: <Folder />,
    label: 'menu.projects',
    feature: 'projects'
  },
  {
    key: '4',
    pathname: appRoutes.dashboard.appsCrypto.base,
    icon: <Coins />,
    label: 'menu.appsCrypto',
    feature: 'cryptoManager'
  },
  {
    key: '5',
    pathname: appRoutes.dashboard.cryptoGest.base,
    icon: <Bitcoin />,
    label: 'menu.cryptoGest',
    feature: 'cryptoGest'
  },
  {
    key: '9',
    pathname: appRoutes.dashboard.poolInvest.base,
    icon: <PiggyBank />,
    label: 'menu.poolInvest',
    feature: 'poolInvest'
  },
  {
    key: '10',
    pathname: appRoutes.dashboard.cryptoBot.base,
    icon: <Bot />,
    label: 'menu.cryptoBot',
    feature: 'cryptoBot'
  },
  {
    key: '6',
    pathname: appRoutes.dashboard.notes.base,
    icon: <NotebookText />,
    label: 'menu.notes',
    feature: 'notes'
  },
  {
    key: '8',
    pathname: appRoutes.dashboard.administration.base,
    icon: <ShieldCheck />,
    label: 'menu.administration',
    adminOnly: true
  },
  {
    key: '7',
    pathname: appRoutes.dashboard.settings.base,
    icon: <Settings />,
    label: 'menu.settings'
  }
];

/**
 * Export `DashboardHeader` component.
 *
 * Layout route that guards authentication and renders the section outlet. The
 * visual shell (sidebar + topbar) now lives in each section's `Dashboard`.
 */

export function DashboardHeader(): JSX.Element | null {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useMemo(() => cookieStorage.get('user'), []);

  useEffect(() => {
    if (pathname === appRoutes.dashboard.base) {
      navigate(appRoutes.dashboard.home.base);
    }
  }, [navigate, pathname]);

  useEffect(() => {
    if (!user) {
      navigate(appRoutes.login);
    }
  }, [navigate, user]);

  if (!user) {
    return null;
  }

  return <Outlet />;
}
